import type { ReactElement } from "react";
import { Routes, Route, Navigate } from "react-router-dom";
import { useAuth } from "./app/auth";
import { WorkspaceProvider, useWorkspace } from "./app/workspace";
import { Shell } from "./components/Shell";
import Login from "./views/Login";
import Dashboard from "./views/Dashboard";
import Dashboards from "./views/Dashboards";
import Accounts from "./views/Accounts";
import AccountDetail from "./views/AccountDetail";
import Relationships from "./views/Relationships";
import Signals from "./views/Signals";
import Tasks from "./views/Tasks";
import Automations from "./views/Automations";
import RiskAgents from "./views/RiskAgents";
import SuccessPlans from "./views/SuccessPlans";
import Roles from "./views/Roles";
import Playbooks from "./views/Playbooks";
import Renewals from "./views/Renewals";
import Surveys from "./views/Surveys";
import Scorecards from "./views/Scorecards";
import Predictions from "./views/Predictions";
import Cockpit from "./views/Cockpit";
import Copilot from "./views/Copilot";
import HealthDesigner from "./views/HealthDesigner";
import Delivery from "./views/Delivery";
import TaskTypes from "./views/TaskTypes";
import ImportData from "./views/ImportData";
import Developers from "./views/Developers";
import Qbr from "./views/Qbr";
import Segments from "./views/Segments";
import Integrations from "./views/Integrations";
import DataStudio from "./views/DataStudio";
import Settings from "./views/Settings";
import Portal from "./views/Portal";
import Project360 from "./views/Project360";
import Relationship360 from "./views/Relationship360";
import Permissions from "./views/Permissions";

/* Route guard: a page whose capability the current user's permission group
 * doesn't grant redirects home, so a hidden nav item can't be reached by URL. */
function Guard({ cap, children }: { cap: string; children: ReactElement }) {
  const { can } = useWorkspace();
  return can(cap) ? children : <Navigate to="/" replace />;
}

function Splash() {
  return (
    <div style={{ height: "100%", display: "grid", placeItems: "center", background: "var(--rail)" }}>
      <svg viewBox="0 0 40 40" width="46" height="46" style={{ animation: "spin 1.4s linear infinite" }}>
        <defs><radialGradient id="sp" cx="50%" cy="50%" r="50%"><stop offset="55%" stopColor="#F2A73B" stopOpacity="0" /><stop offset="82%" stopColor="#F2A73B" stopOpacity="0.8" /><stop offset="100%" stopColor="#F2A73B" stopOpacity="0" /></radialGradient></defs>
        <circle cx="20" cy="20" r="18" fill="url(#sp)" />
        <circle cx="20" cy="20" r="11" fill="#0C0D13" stroke="#F2A73B" strokeWidth="1.4" />
        <circle cx="24.5" cy="16" r="11" fill="#0C0D13" />
      </svg>
    </div>
  );
}

export default function App() {
  // The customer portal is public — it must render outside the auth gate.
  return (
    <Routes>
      <Route path="/portal/:token" element={<Portal />} />
      <Route path="/*" element={<AuthedApp />} />
    </Routes>
  );
}

function AuthedApp() {
  const { me, loading } = useAuth();
  if (loading) return <Splash />;
  if (!me) return <Login />;

  return (
    <WorkspaceProvider>
      <Routes>
        <Route element={<Shell />}>
          <Route index element={<Dashboard />} />
          <Route path="dashboards" element={<Guard cap="insights"><Dashboards /></Guard>} />
          <Route path="accounts" element={<Guard cap="customers"><Accounts /></Guard>} />
          <Route path="accounts/:id" element={<Guard cap="customers"><AccountDetail /></Guard>} />
          <Route path="projects/:id" element={<Guard cap="customers"><Project360 /></Guard>} />
          <Route path="relationships" element={<Guard cap="customers"><Relationships /></Guard>} />
          <Route path="relationships/:id" element={<Guard cap="customers"><Relationship360 /></Guard>} />
          <Route path="signals" element={<Guard cap="insights"><Signals /></Guard>} />
          <Route path="tasks" element={<Guard cap="execution"><Tasks /></Guard>} />
          <Route path="automations" element={<Guard cap="execution"><Automations /></Guard>} />
          <Route path="risk-agents" element={<Guard cap="insights"><RiskAgents /></Guard>} />
          <Route path="success-plans" element={<Guard cap="customers"><SuccessPlans /></Guard>} />
          <Route path="playbooks" element={<Guard cap="execution"><Playbooks /></Guard>} />
          <Route path="renewals" element={<Guard cap="revenue"><Renewals /></Guard>} />
          <Route path="surveys" element={<Guard cap="revenue"><Surveys /></Guard>} />
          <Route path="scorecards" element={<Guard cap="team_analytics"><Scorecards /></Guard>} />
          <Route path="predictions" element={<Guard cap="insights"><Predictions /></Guard>} />
          <Route path="cockpit" element={<Cockpit />} />
          <Route path="copilot" element={<Copilot />} />
          <Route path="health-designer" element={<Guard cap="settings"><HealthDesigner /></Guard>} />
          <Route path="delivery" element={<Guard cap="settings"><Delivery /></Guard>} />
          <Route path="task-types" element={<Guard cap="settings"><TaskTypes /></Guard>} />
          <Route path="qbr" element={<Guard cap="revenue"><Qbr /></Guard>} />
          <Route path="roles" element={<Guard cap="governance"><Roles /></Guard>} />
          <Route path="permissions" element={<Guard cap="governance"><Permissions /></Guard>} />
          <Route path="segments" element={<Guard cap="settings"><Segments /></Guard>} />
          <Route path="integrations" element={<Guard cap="integrations"><Integrations /></Guard>} />
          <Route path="data" element={<Guard cap="integrations"><DataStudio /></Guard>} />
          <Route path="import" element={<Guard cap="integrations"><ImportData /></Guard>} />
          <Route path="developers" element={<Guard cap="developers"><Developers /></Guard>} />
          <Route path="settings" element={<Settings />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Route>
      </Routes>
    </WorkspaceProvider>
  );
}
